import React, { useCallback, useContext, useState } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import { loginUser } from '../../queries';
import AuthContext from '../../contexts/AuthContext';
import './AuthModal.scss';

interface LoginModalProps {
  show: boolean;
  onHide: () => void;
}

export const LoginModal: React.FC<LoginModalProps> = ({ show, onHide }) => {
  const { setUser } = useContext(AuthContext);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const reset = useCallback(() => {
    setEmail('');
    setPassword('');
    setError('');
  }, []);

  const handleHide = useCallback(() => {
    reset();
    onHide();
  }, [reset, onHide]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setError('');

      if (!email.trim() || !password) {
        setError('Please enter your email and password.');
        return;
      }

      setLoading(true);
      try {
        const result = await loginUser(email.trim(), password);
        if (result.success) {
          setUser({ firstName: result.firstName || '', emailId: result.emailId || email.trim(), uid: result.uid || 0 });
          handleHide();
        } else {
          setError(result.message || 'Invalid email or password.');
        }
      } finally {
        setLoading(false);
      }
    },
    [email, password, setUser, handleHide],
  );

  return (
    <Modal show={show} onHide={handleHide} centered dialogClassName="auth-modal">
      <Modal.Header closeButton>
        <Modal.Title>Log in</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit} noValidate>
          <Form.Group className="mb-3">
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              autoComplete="email"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Password</Form.Label>
            <Form.Control
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              autoComplete="current-password"
            />
          </Form.Group>
          {error && <p className="text-danger">{error}</p>}
          <Button type="submit" variant="primary" disabled={loading}>
            {loading ? 'Logging in...' : 'Login'}
          </Button>
        </Form>
      </Modal.Body>
    </Modal>
  );
};
